import {
  Activity,
  Eye,
  Handshake,
  MailOpen,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import type { TimelineEvent, TimelineEventKind } from "@colab/shared";

const ICON_BY_KIND: Record<TimelineEventKind, LucideIcon> = {
  deal_attributed: Handshake,
  deal_won: Trophy,
  content_view: Eye,
  email_opened: MailOpen,
};

const TONE_BY_KIND: Record<TimelineEventKind, string> = {
  deal_attributed: "text-accent bg-accent/10",
  deal_won: "text-status-won bg-status-won/10",
  content_view: "text-muted-foreground bg-muted",
  email_opened: "text-status-active bg-status-active/10",
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

export function ActivityTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return (
      <section className="rounded-xl border bg-card p-5">
        <header className="mb-4">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Activity
          </h2>
        </header>
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed bg-muted/20 py-10 text-xs text-muted-foreground">
          <Activity className="h-4 w-4" />
          No recent activity in this window.
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border bg-card p-5">
      <header className="mb-4">
        <h2 className="font-display text-base font-semibold tracking-tight">
          Activity
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Recent events across deals and content for this partner.
        </p>
      </header>

      <ul className="space-y-3">
        {events.map((event) => {
          const Icon = ICON_BY_KIND[event.kind];
          const tone = TONE_BY_KIND[event.kind];
          return (
            <li
              key={event.id}
              className="flex items-start gap-3 rounded-lg border border-transparent px-2 py-1.5 transition hover:border-border hover:bg-muted/30"
            >
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone}`}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{event.title}</p>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  {event.meta && <span>{event.meta}</span>}
                  {event.meta && <span aria-hidden>·</span>}
                  <span>{timeAgo(event.at)}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
